import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import axios from "axios";
import { BASE_URL, HEADERS } from "../../utils/constant";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import Pagination from "../../components/Pagination";
import ExportModal from "../../components/Modals/ExportsModel/main";
import { FaFilePdf, FaFileExport } from "react-icons/fa";
import CommonButton from "../../components/CommonButton";

const InvoiceList = () => {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [allIds, setAllIds] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    fetchInvoices(currentPage);
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchInvoices(1);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchText]);

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectInvoice = (invoiceNo) => {
    setSelectedIds((prev) =>
      prev.includes(invoiceNo)
        ? prev.filter((i) => i !== invoiceNo)
        : [...prev, invoiceNo],
    );
  };

  useEffect(() => {
    fetchAllInvoiceIds();
  }, [searchText]);

  const fetchAllInvoiceIds = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/users/subscriptions/invoice-data/`,
        {
          headers: HEADERS,
          params: {
            page: 1,
            page_size: 100000,
            search: searchText,
          },
        },
      );

      const data = response?.data?.results || [];
      setAllIds(data.map((item) => item.invoice_no));
    } catch (error) {
      
    }
  };

  const fetchInvoices = async (page = 1) => {
    setLoading(true);

    try {
      const response = await axios.get(
        `${BASE_URL}/users/subscriptions/invoice-data/`,
        {
          headers: HEADERS,
          params: {
            page: page,
            page_size: itemsPerPage,
            search: searchText,
          },
        },
      );

      setInvoices(response.data?.results || []);
      setTotalPages(response.data?.total_pages || 0);
      setCurrentPage(page);
    } catch (error) {
      if (error.response?.data?.detail) {
        setInvoices([]);
        setTotalPages(0);
      } else {
        console.log(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageClick = (selectedPage) => {
    const newPage = selectedPage.selected + 1;
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (event) => {
    const value = Number(event.target.value);
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  return (
    <div className="container-fluid">
      <div className="mt-3 d-flex container-header justify-content-between mb-3">
        <h4>Invoices</h4>

        <div className="d-flex gap-2">
          <Form.Control
            placeholder="Search Here..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="uniform-input"
          />

          <CommonButton
            className="uniform-button"
            onClick={() => setShowExportModal(true)}
            disabled={invoices.length === 0}
          >
            <FaFileExport /> Export
          </CommonButton>
        </div>
      </div>

      <div className="table-responsive tabell">
        {loading ? (
          <Loader />
        ) : invoices.length > 0 ? (
          <table className="table tstart">
            <thead className="thead">
              <tr>
                <th style={{ width: "50px" }}>
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={
                      selectedIds.length === allIds.length && allIds.length > 0
                    }
                  />
                </th>
                <th style={{ width: "200px" }}>Invoice No</th>
                <th style={{ width: "300px" }}>Customer Name</th>
                <th style={{ width: "300px" }}>Email</th>
                <th style={{ width: "180px" }}>Plan</th>
                <th style={{ width: "150px" }}>User Limit</th>
                <th style={{ width: "150px" }}>SMS Limit</th>
                <th style={{ width: "180px" }}>Total Cost</th>
                <th style={{ width: "150px" }}>GST</th>
                <th style={{ width: "180px" }}>Payment ID</th>
                <th style={{ width: "180px" }}>Start Date</th>
                <th style={{ width: "180px" }}>End Date</th>
                <th style={{ width: "180px" }}>Invoice Date</th>
                <th style={{ width: "120px" }}>Download</th>
              </tr>
            </thead>

            <tbody>
              {invoices.map((invoice) => {
                const fullName =
                  invoice?.user?.first_name || invoice?.user?.last_name
                    ? `${invoice?.user?.first_name || ""} ${
                        invoice?.user?.last_name || ""
                      }`.trim()
                    : "—";

                return (
                  <tr key={invoice.invoice_no}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(invoice.invoice_no)}
                        onChange={() => handleSelectInvoice(invoice.invoice_no)}
                      />
                    </td>

                    <td>{invoice.invoice_no || "—"}</td>
                    

                    <td>{fullName}</td>

                    <td>{invoice?.user?.email || "—"}</td>

                    <td>{invoice.plan || "—"}</td>
                    <td>{invoice.user_limit || "—"}</td>
                    <td>{invoice.sms_limit || "—"}</td>

                    <td>
                      {invoice.amount
                        ? `₹${invoice.amount.toLocaleString()}`
                        : "—"}
                    </td>

                    <td>
                      {invoice.gst_percent
                        ? `₹${invoice.gst_percent.toLocaleString()}`
                        : "—"}
                    </td>

                    <td>{invoice.payment_id || "—"}</td>
                    <td>{invoice.subscription_start_date || "—"}</td>
                    <td>{invoice.subscription_end_date || "—"}</td>
                    <td>{invoice.invoice_date}</td>

                    <td>
                      {invoice.pdf_url ? (
                        <FaFilePdf
                          style={{ color: "red", cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(invoice.pdf_url, "_blank");
                          }}
                        />
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <NoData message="No Invoices Found" height="400px" />
        )}
      </div>

      {invoices.length > 0 && (
        <Pagination
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          handlePageClick={handlePageClick}
          handleItemsPerPageChange={handleItemsPerPageChange}
        />
      )}

      <ExportModal
        show={showExportModal}
        handleClose={() => setShowExportModal(false)}
        title="invoices"
        selectedInvoiceIds={selectedIds}
        setSelectedInvoiceIds={setSelectedIds}
      />
    </div>
  );
};

export default InvoiceList;
